import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setToken } from "../lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [identity, setIdentity] = useState(null);

  const fetchIdentity = useCallback(async () => {
    try {
      const { data } = await api.get("/me/identity");
      setIdentity(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      fetchIdentity();
      return data;
    } catch {
      setUser(false);
      setIdentity(null);
      return false;
    }
  }, [fetchIdentity]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    await fetchIdentity();
    return data.user;
  };

  const register = async (artist_name, email, password) => {
    const { data } = await api.post("/auth/register", { artist_name, email, password });
    return data;
  };

  const verifyEmail = async (email, code) => {
    const { data } = await api.post("/auth/verify-email", { email, code });
    setToken(data.access_token);
    setUser(data.user);
    await fetchIdentity();
    return data.user;
  };

  const resendCode = async (email) => api.post("/auth/resend-code", { email });

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(false);
    setIdentity(null);
  };

  const switchSpace = async (space) => {
    await api.post("/me/active-space", { space });
    // Recharger l'identité complète: les permissions/features dépendent de l'espace actif.
    const fresh = await fetchIdentity();
    if (!fresh) setIdentity((p) => (p ? { ...p, active_space: space } : p));
    return space;
  };

  const becomeManager = async () => {
    await api.post("/roles/become-manager");
    return await fetchIdentity();
  };

  const activeSpace = identity?.active_space || "CREATOR";

  // --- Phase 1: helpers socle permissions (rétrocompatibles, purement UX) ---
  const permissions = useMemo(() => identity?.permissions || [], [identity]);
  const features = useMemo(() => identity?.features || {}, [identity]);
  const can = useCallback(
    (permission) => permissions.includes(permission),
    [permissions]
  );
  const hasProfile = useCallback(
    (profile) => (identity?.profiles || []).includes(profile),
    [identity]
  );
  const featureState = useCallback(
    (key) => features[key]?.state || "HIDDEN",
    [features]
  );

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, verifyEmail, resendCode, logout, refresh, identity, fetchIdentity, activeSpace, switchSpace, becomeManager, permissions, features, can, hasProfile, featureState }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
